import {
    BasketSummary,
    CheckoutState,
    CompanyLookupResponse,
    OrderSuccessResponse,
    VatValidateResponse,
} from '../types';

/**
 * Klient checkout API (/api/v1/checkout/*). Session cookie košíku jde
 * automaticky (same-origin), hlavička X-Requested-With: fetch je povinná
 * CSRF ochrana (server jinak vrací 403).
 */

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly code: string,
        message: string,
        public readonly fields?: Record<string, string>,
        public readonly context?: Record<string, unknown>,
    ) {
        super(message);
    }
}

async function call<T>(url: string, options: RequestInit = {}, signal?: AbortSignal): Promise<T> {
    const response = await fetch(url, {
        credentials: 'same-origin',
        signal,
        ...options,
        headers: {
            // 'XMLHttpRequest' kvůli Tracy isAjax() — v dev módu jinak Tracy echuje
            // deprecations do JSON odpovědi a rozbije parsování
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            ...(options.headers ?? {}),
        },
    });

    const body = await response.json().catch(() => null);

    if (!body || typeof body.ok !== 'boolean') {
        throw new ApiError(response.status, 'INVALID_RESPONSE', 'Unexpected server response');
    }

    if (!body.ok) {
        throw new ApiError(
            response.status,
            body.error?.code ?? 'UNKNOWN',
            body.error?.message ?? 'Unknown error',
            body.error?.fields,
            body.error?.context,
        );
    }

    return body.data as T;
}

export function createCheckoutApi(baseUrl: string) {
    return {
        getState: (signal?: AbortSignal) =>
            call<CheckoutState>(`${baseUrl}/state`, {}, signal),

        companyLookup: (ic: string, signal?: AbortSignal) =>
            call<CompanyLookupResponse>(`${baseUrl}/company-lookup`, {
                method: 'POST',
                body: JSON.stringify({ ic }),
            }, signal),

        vatValidate: (dic: string, signal?: AbortSignal) =>
            call<VatValidateResponse>(`${baseUrl}/vat-validate`, {
                method: 'POST',
                body: JSON.stringify({ dic }),
            }, signal),

        vatChoice: (payer: boolean, dic: string) =>
            call<{ accepted: boolean; reverseCharge: boolean; basketSummary: BasketSummary }>(`${baseUrl}/vat-choice`, {
                method: 'POST',
                body: JSON.stringify({ payer, dic }),
            }),

        emailCheck: (email: string, signal?: AbortSignal) =>
            call<{ exists: boolean; validEmail: boolean }>(`${baseUrl}/email-check`, {
                method: 'POST',
                body: JSON.stringify({ email }),
            }, signal),

        icExistCheck: (ic: string, signal?: AbortSignal) =>
            call<{ exists: boolean }>(`${baseUrl}/ic-exist-check`, {
                method: 'POST',
                body: JSON.stringify({ ic }),
            }, signal),

        phoneValidate: (phone: string, signal?: AbortSignal) =>
            call<{ valid: boolean; normalized: string | null }>(`${baseUrl}/phone-validate`, {
                method: 'POST',
                body: JSON.stringify({ phone }),
            }, signal),

        basketSummary: (signal?: AbortSignal) =>
            call<BasketSummary>(`${baseUrl}/basket-summary`, {}, signal),

        createOrder: (payload: unknown, idempotencyKey: string) =>
            call<OrderSuccessResponse>(`${baseUrl}/order`, {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: { 'Idempotency-Key': idempotencyKey },
            }),
    };
}

export type CheckoutApi = ReturnType<typeof createCheckoutApi>;
